import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, FileText, Plus, Trash2, ExternalLink, Share2, RefreshCw,
} from 'lucide-react'

import {
  createDocument,
  deleteDocument,
  listDocuments,
  type DocumentSummary,
} from '@/api/documents'
import { useAuth } from '@/auth/AuthContext'
import api from '@/api/client'
import ShareDialog from './ShareDialog'

interface PageOption {
  obj_name: string
  title: string
}

/**
 * List documents for a project + launch new ones from any document-mode
 * page in the project. Lives at /documents/:project.
 */
export default function DocumentListPage() {
  const { project } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [documents, setDocuments] = useState<DocumentSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [pages, setPages] = useState<PageOption[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [creating, setCreating] = useState<string | null>(null)
  const [shareTarget, setShareTarget] = useState<DocumentSummary | null>(null)

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const docs = await listDocuments({ project })
      setDocuments(docs)
    } catch {
      toast.error('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { load() }, [load])

  // Fetch the project's smart pages so we can offer document-mode ones
  // as templates. We hit the existing srceditor file-tree endpoint and
  // load each model to read pageMode — fine for small projects; can be
  // promoted to a dedicated backend endpoint later if needed.
  useEffect(() => {
    if (!project) return
    let cancelled = false
    const loadPages = async () => {
      try {
        const { data } = await api.get('/srceditor/file-tree', { params: { project } })
        const tree = data?.tree
        const stack: any[] = Array.isArray(tree) ? [...tree] : []
        const found: PageOption[] = []
        while (stack.length > 0) {
          const node = stack.pop()
          if (!node) continue
          if (node.type === 'file' && /\.json$/.test(node.name) && /page_models|Custom_Pages|Page_Design/.test(node.path || '')) {
            const objName = node.name.replace(/\.json$/, '')
            try {
              const res = await api.get('/designers/load-page-object', { params: { project, obj_name: objName } })
              const content = res.data?.content
              const page = content?.page ?? content
              // Resolve `kind` with the legacy-pageMode fallback (mirrors
              // backend `resolve_page_kind`). Only document-template pages
              // can spawn SmartDocument instances.
              const resolvedKind = page?.kind
                ?? (page?.pageMode === 'document' ? 'document' : 'internal')
              if (page && resolvedKind === 'document') {
                found.push({ obj_name: objName, title: page.title || objName })
              }
            } catch { /* skip pages we can't read */ }
          }
          if (Array.isArray(node.children)) stack.push(...node.children)
        }
        if (!cancelled) setPages(found)
      } catch {
        // Best-effort — if file tree isn't available, the picker stays empty.
      }
    }
    loadPages()
    return () => { cancelled = true }
  }, [project])

  const runtimeBaseUrl = useMemo(() => {
    return (window as any).__TWASTA_RUNTIME_URL__ || '/runtime'
  }, [])

  const handleCreate = async (page: PageOption) => {
    if (!project) return
    setCreating(page.obj_name)
    try {
      const doc = await createDocument({ project, page_obj_name: page.obj_name })
      setPickerOpen(false)
      toast.success('Document created')
      // Navigate to the runtime route for this document.
      window.open(`${runtimeBaseUrl}/documents/${doc.id}`, '_blank')
      load()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to create document'
      toast.error(msg)
    } finally {
      setCreating(null)
    }
  }

  const handleDelete = async (doc: DocumentSummary) => {
    if (!confirm(`Delete document "${doc.title}"? This cannot be undone.`)) return
    try {
      await deleteDocument(doc.id)
      toast.success('Document deleted')
      load()
    } catch {
      toast.error('Failed to delete document')
    }
  }

  if (!project) return null

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 23, margin: 0, color: 'var(--color-text)' }}>Documents · {project}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--color-text-secondary)' }}>
            Notion-style instances of document-mode pages.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={load}
            className="btn btn-ghost btn-sm"
            title="Refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="btn btn-primary btn-sm"
          >
            <Plus size={14} /> New document
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)' }}>
          <Loader2 size={14} className="spin" /> Loading…
        </div>
      ) : documents.length === 0 ? (
        <div style={{
          padding: 40, border: '1px dashed var(--color-border)', borderRadius: 8,
          textAlign: 'center', color: 'var(--color-text-secondary)',
        }}>
          <FileText size={32} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: 8 }}>No documents yet. Create one from a document-mode page above.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {documents.map(doc => (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '10px 14px',
              border: '1px solid var(--color-border)', borderRadius: 8,
              background: 'var(--color-surface)',
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={14} style={{ color: 'var(--color-text-secondary)' }} />
                  <strong style={{ fontSize: 15 }}>{doc.title}</strong>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>· {doc.page_obj_name}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                  {doc.sharing_mode} · updated {doc.updated_at?.slice(0, 19) || '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => window.open(`${runtimeBaseUrl}/documents/${doc.id}`, '_blank')}
                  title="Open"
                >
                  <ExternalLink size={13} /> Open
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShareTarget(doc)}
                  title="Share"
                  disabled={doc.owner_id !== user?.user_id}
                >
                  <Share2 size={13} /> Share
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleDelete(doc)}
                  title="Delete"
                  disabled={doc.owner_id !== user?.user_id}
                  style={{ color: 'var(--color-error)' }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 8, padding: 18, width: 480, maxHeight: '70vh', overflow: 'auto',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>Create document from page</h3>
            <p style={{ marginTop: 4, marginBottom: 12, fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Only pages with <code>pageMode = document</code> are listed.
            </p>
            {pages.length === 0 ? (
              <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: 13, textAlign: 'center' }}>
                No document-mode pages found. Mark a page as Document in the page designer first.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {pages.map(p => (
                  <button
                    key={p.obj_name}
                    type="button"
                    onClick={() => handleCreate(p)}
                    disabled={creating !== null}
                    style={{
                      textAlign: 'left', padding: '8px 12px', borderRadius: 6,
                      border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                      color: 'var(--color-text)', cursor: creating ? 'wait' : 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{p.obj_name}</div>
                  </button>
                ))}
              </div>
            )}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setPickerOpen(false)} className="btn btn-ghost btn-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {shareTarget && (
        <ShareDialog
          document={shareTarget}
          onClose={() => setShareTarget(null)}
          onChange={() => load()}
        />
      )}
    </div>
  )
}
